import { useState } from 'react';

import { Flex, Grid } from '@chakra-ui/react';
import { RRuleDayMap } from 'Deer/components/common/RecurrenceSelect/helper';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { format } from 'date-fns';
import { RRule } from 'rrule';
import { ImgIconTag } from 'screens/admin/Reviews/ReviewProcessV2Intent/ReviewProcessV2Presets';
import { getWhatDayOfTheMonth } from 'screens/oneOnOneRabbit/InstancePage/common/modals/CustomRecurrenceModal';

import { Onetime } from 'assets/assetsV1/iconComponents/Onetime';
import { Recurring } from 'assets/assetsV1/iconComponents/Recurring';
import MeshLogoImage from 'assets/assetsV1/icons/specialIcons/meshLogo.svg';
import customSurveyImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/customSurvey.png';
import eNPSSurveyImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/eNPSSurvey.png';
import employeeEngagementPulseImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/employeeEngagementPulse.png';
import employeeEngagementSurveyImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/employeeEngagementSurvey.png';
import employeeRecognitionAndGrowthImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/employeeRecognitionAndGrowth.png';
import managerFeedbackSurveyImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/managerFeedbackSurvey.png';
import mentalHealthAndWellbeingImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/mentalHealthAndWellbeing.png';
import retentionRiskCheckImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/retentionRiskCheck.png';
import schedulingSatisfactionImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/schedulingSatisfaction.png';
import skillsAndTrainingImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/skillsAndTraining.png';
import workLifeBalanceImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/workLifeBalance.png';
import workplaceSafetyCheckImage from 'assets/assetsV1/illustrations/deerIllustrations/surveyPresetTypes/workplaceSafetyCheck.png';

import { isNetchexLogin } from 'constants/netChexConstants';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { RadioCard } from 'uiToolkitV1/RadioCard/RadioCard';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextLabel, TextLabelSmall, TextBodyMedium } from 'uiToolkitV1/Typography';

import { SurveyPresetsFooter } from './SurveyPresetsFooter';
import { FREQUENCY_TYPES } from './types';

export const RECURRENCE_TYPES = { WEEKLY: 'WEEKLY', MONTHLY: 'MONTHLY', QUARTERLY: 'QUARTERLY' } as const;
export const RECURRENCE_TYPES_LABEL = {
  WEEKLY: 'Once every week',
  MONTHLY: 'Once every month',
  QUARTERLY: 'Once every 3 months',
} as const;

export const SURVEY_PRESETS = [
  {
    type: 'EMPLOYEE_ENGAGEMENT_PULSE',
    title: 'Employee Engagement Pulse',
    subText:
      'A short, weekly check-in capturing employee sentiment and motivation to track changes and respond in real time.',
    imgSrc: employeeEngagementPulseImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.WEEKLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      return new RRule({
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay]],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: true,
    templateId: 5602,
  },
  {
    type: 'EMPLOYEE_ENGAGEMENT_SURVEY',
    title: 'Employee Engagement Survey',
    subText:
      'A detailed quarterly review exploring satisfaction, alignment, and engagement drivers across the workforce.',
    imgSrc: employeeEngagementSurveyImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: true,
    templateId: 5293,
  },
  {
    type: 'WORKPLACE_SAFETY_CHECK',
    title: 'Workplace Safety Check',
    subText:
      'A recurring pulse to assess physical working conditions, safety compliance, and employee perception of safety.',
    imgSrc: workplaceSafetyCheckImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.MONTHLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: false,
    templateId: 5602,
  },
  {
    type: 'MANAGER_FEEDBACK_CHECK',
    title: 'Manager Feedback Survey',
    subText: 'A quarterly feedback cycle focused on manager communication, support, and leadership effectiveness.',
    imgSrc: managerFeedbackSurveyImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: true,
    templateId: 5293,
  },
  {
    type: 'STRESS_WFB_CHECK',
    title: 'Work-Life Balance & Job Stress Survey',
    subText:
      'A structured check to evaluate workload manageability and how well employees are balancing work with life.',
    imgSrc: workLifeBalanceImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: false,
    templateId: 5602,
  },
  {
    type: 'RECOGNITION_GROWTH_CHECK',
    title: 'Employee Recognition & Growth Check',
    subText: 'Quarterly feedback on whether employees feel valued and see meaningful opportunities to learn and grow.',
    imgSrc: employeeRecognitionAndGrowthImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: false,
    templateId: 5293,
  },
  {
    type: 'SKILLS_TRAINING_NEEDS_ASSESSMENT',
    title: 'Skills & Training Needs Assessment',
    subText:
      'A focused, one-time survey to understand employees’ skill gaps, training preferences, and development needs.',
    imgSrc: skillsAndTrainingImage,
    frequencyType: FREQUENCY_TYPES.ONE_TIME,
    recurrenceType: null,
    recurrenceRule: () => null,
    isMeshRecommended: false,
    templateId: 5293,
  },
  {
    type: 'RETENTION_RISK_CHECK',
    title: 'Retention Risk Check',
    subText: 'A quarterly pulse identifying early warning signs for disengagement or attrition risk across teams.',
    imgSrc: retentionRiskCheckImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: true,
    templateId: 5602,
  },
  {
    type: 'MENTAL_HEALTH_CHECK_IN',
    title: 'Mental Health & Wellbeing Check-In',
    subText:
      'A regular check-in to understand how employees are feeling mentally and emotionally, and what support they need.',
    imgSrc: mentalHealthAndWellbeingImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.MONTHLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: true,
    templateId: 5602,
  },
  {
    type: 'SCHEDULING_SATISFACTION',
    title: 'Scheduling Satisfaction Survey',
    subText: 'A monthly review to evaluate how predictable, flexible, and employee-friendly work schedules feel.',
    imgSrc: schedulingSatisfactionImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.MONTHLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 1,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: false,
    templateId: 5293,
  },
  {
    type: 'ENPS',
    title: 'eNPS Survey',
    subText:
      'A quick loyalty check using the Net Promoter Score to gauge how likely employees are to recommend your company.',
    imgSrc: eNPSSurveyImage,
    frequencyType: FREQUENCY_TYPES.RECURRING,
    recurrenceType: RECURRENCE_TYPES.QUARTERLY,
    recurrenceRule: (date: Date) => {
      const selectedDay = format(date, 'EEEE');
      const { nthDayOfTheMonth } = getWhatDayOfTheMonth(date);

      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byweekday: [RRuleDayMap[selectedDay].nth(nthDayOfTheMonth)],
      })
        .toString()
        .split('RRULE:')[1];
    },
    isMeshRecommended: false,
    templateId: 5584,
  },
  {
    type: null,
    title: 'Custom Survey',
    subText: 'A flexible survey setup allowing you to customize all elements of the survey as needed.',
    imgSrc: customSurveyImage,
    frequencyType: null,
    recurrenceType: null,
    recurrenceRule: () => null,
    isMeshRecommended: false,
    templateId: null,
  },
];

const SurveyCreationPresets = () => {
  const [selectedSurveyCreationPreset, setSelectedSurveyCreationPreset] = useState(null);

  const isL2NavVisible = useSelectIsL2Visible();

  const handlePresetChange = (selectedPreset) => {
    setSelectedSurveyCreationPreset(selectedPreset);
  };

  const getAdditionalTags = ({ isMeshRecommended, recurrenceType, frequencyType }) => {
    const recurrenceTypeLabel = RECURRENCE_TYPES_LABEL[recurrenceType] ?? '';
    return (
      <Flex gap={1}>
        {recurrenceType && frequencyType === FREQUENCY_TYPES.RECURRING && (
          <ImgIconTag label={`Recurring - ${recurrenceTypeLabel}`} Icon={Recurring} />
        )}
        {frequencyType && frequencyType === FREQUENCY_TYPES.ONE_TIME && <ImgIconTag label="One time" Icon={Onetime} />}
        {isMeshRecommended && (
          <ImgIconTag
            label={isNetchexLogin ? 'Recommended' : 'Mesh recommended'}
            imgSrc={isNetchexLogin ? null : MeshLogoImage}
            withImagePadding
          />
        )}
      </Flex>
    );
  };

  return (
    <PaperWithShadow
      display="flex"
      flexDir="column"
      py={0}
      flex={1}
      overflow="auto"
      ml={isL2NavVisible || isNetchexLogin ? '0' : '34px'}
      transition="margin .3s ease;"
    >
      <Flex flex={1} p={4} overflow="auto" borderRadius="8px" bg="white" flexDir="column">
        <Flex alignItems="center" gap={2}>
          <TextBodyMedium bold>What type of survey do you want to launch?</TextBodyMedium>
          <BetaTag />
        </Flex>

        <TextLabel color="text.g2" mb={3}>
          Pick the survey type you want to launch and modify the pre-filled form according to your needs in the next
          step.
        </TextLabel>

        <Flex flex={1} p={2} borderRadius="8px" bg="surface.F5F7F9" flexDir="column" gap={2}>
          <Grid gridTemplateColumns="1fr 1fr" gridGap={2}>
            {SURVEY_PRESETS.map(
              ({ type, title, subText, recurrenceType, isMeshRecommended, imgSrc, frequencyType }) => (
                <RadioCard
                  key={type}
                  title={title}
                  subText={subText}
                  imgSrc={imgSrc}
                  onClick={() => handlePresetChange(type)}
                  checked={selectedSurveyCreationPreset === type}
                  additionalContent={getAdditionalTags({ recurrenceType, isMeshRecommended, frequencyType })}
                />
              )
            )}
          </Grid>
        </Flex>
      </Flex>
      <SurveyPresetsFooter selectedSurveyCreationPreset={selectedSurveyCreationPreset} />
    </PaperWithShadow>
  );
};

export { SurveyCreationPresets };
